import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { FocActionRow } from '~/components/Patient';
import { ServicesContext } from '~/services';
import {
    sortFocActions,
    focGroupingValidation,
    buildFocSubmissionAnswers,
    gapIsPending,
} from '~/util';

const propTypes = {
    focGap: PropTypes.shape({
        measure: PropTypes.shape({
            focs: PropTypes.arrayOf(
                PropTypes.shape({
                    instruction: PropTypes.string,
                    type: PropTypes.shape({
                        id: PropTypes.number,
                        description: PropTypes.string,
                    }),
                    foc_actions: PropTypes.arrayOf(
                        PropTypes.shape({
                            name: PropTypes.string,
                            sequence: PropTypes.number,
                            foc_action_details: PropTypes.arrayOf(
                                PropTypes.shape({
                                    id: PropTypes.number,
                                })
                            ),
                        })
                    ),
                })
            ),
        }),
        id: PropTypes.number.isRequired,
        status: PropTypes.number.isRequired,
        submission_answer: PropTypes.shape({
            id: PropTypes.number,
            status: PropTypes.number.isRequired,
        }),
        type: PropTypes.number.isRequired,
        updated_at: PropTypes.string.isRequired,
    }).isRequired,
    formId: PropTypes.string.isRequired,
    patientId: PropTypes.number.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    setFormDirty: PropTypes.func.isRequired,
    setSaving: PropTypes.func.isRequired,
    setDisabled: PropTypes.func.isRequired,
};

export function FocGapForm({
    focGap,
    formId,
    patientId,
    onSubmitSuccess,
    setFormDirty,
    setDisabled,
    setSaving,
}) {
    const context = React.useContext(ServicesContext);
    const [formValues, setFormValues] = React.useState({});
    const dirtyFlag = React.useRef(false);
    const submitted = React.useRef(false);

    function handleFormInputChange(id, value) {
        setFormValues((prev) => ({
            ...prev,
            [id]: value,
        }));
    }

    async function handleSave(values) {
        const responses = buildFocSubmissionAnswers(values);
        if (responses.length === 0) return;
        try {
            setSaving(true);
            setDisabled(true);
            await context.patient.updateFocGap(
                focGap.id,
                patientId,
                {
                    submission_answer: { metadata: { responses } },
                    updated_at: focGap.updated_at,
                },
                // Explicitly pop-up ApiErrorResponse modal to handle backend validation errors
                { handledErrors: { 400: false } }
            );
            onSubmitSuccess();
        } catch (error) {
            setSaving(false);
            setDisabled(false);
        }
    }

    return (
        <Formik
            enableReinitialize={!submitted.current}
            initialValues={formValues}
            validateOnChange
            validateOnBlur={submitted.current}
            onSubmit={handleSave}
            validate={(values) => focGroupingValidation(values, focGap.measure)}
        >
            {({ dirty, isSubmitting }) => {
                if (!submitted.current && isSubmitting) submitted.current = true;
                if (dirtyFlag.current !== dirty) {
                    dirtyFlag.current = dirty;
                    // timeout allows the state update to run on the next tick after render
                    setTimeout(() => {
                        setFormDirty(dirty);
                    }, 0);
                }
                return (
                    <Form id={formId}>
                        <table className="gap-table foc-gap-table align-middle">
                            <thead>
                                <tr>
                                    <th className="checkbox-col">
                                        <span className="sr-only">Checkbox column</span>
                                    </th>
                                    <th className="col-gap-action-name">Measure</th>
                                    <th className="w-20">Acceptable Dates</th>
                                    <th className="w-10">Frequency</th>
                                </tr>
                            </thead>
                            <tbody>
                                {focGap.measure.focs.map((foc) =>
                                    foc.foc_actions
                                        .sort(sortFocActions)
                                        .map((fga) => (
                                            <FocActionRow
                                                key={fga.id}
                                                action={fga}
                                                submissionAnswer={focGap.submission_answer}
                                                handleChange={handleFormInputChange}
                                                isSingle={
                                                    foc.foc_actions.length === 1 &&
                                                    focGap.measure.focs.length === 1
                                                }
                                                readOnly={!gapIsPending(focGap)}
                                            />
                                        ))
                                )}
                            </tbody>
                        </table>
                    </Form>
                );
            }}
        </Formik>
    );
}

FocGapForm.propTypes = propTypes;
